import { Grid, Typography } from "@mui/material";
import { PhoneInputField, Select, TextField } from "components/UI";
import { ADDRESSES, useMembersStore } from "features/members";
import { useEffect } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCities, useCountries, useRegions, useZones } from "../../api";

export const EmergencyContactSection = ({
  register,
  errors,
  watch,
  control,
  setValue,
  isEditMode
}) => {
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const { t } = useTranslation();

  const countriesQuery = useCountries();
  const regionsQuery = useRegions({
    countryId: watch("emergency_contact_country_id"),
    config: {
      enabled: !!watch("emergency_contact_country_id")
    }
  });
  const zonesQuery = useZones({
    regionId: watch("emergency_contact_region_id"),
    config: {
      enabled: !!watch("emergency_contact_region_id")
    }
  });
  const citiesQuery = useCities({
    zoneId: watch("emergency_contact_zone_id"),
    config: {
      enabled: !!watch("emergency_contact_zone_id")
    }
  });

  useEffect(() => {
    if (countriesQuery.data) {
      if (isEditMode && fullMemberData) {
        setValue(
          "emergency_contact_country_id",
          String(fullMemberData?.emergency_contact_country_id || "")
        );
      } else {
        setValue("emergency_contact_country_id", "66");
      }
    }
  }, [countriesQuery.data, isEditMode, fullMemberData]);

  useEffect(() => {
    if (regionsQuery.data && isEditMode && fullMemberData) {
      setValue(
        "emergency_contact_region_id",
        String(fullMemberData?.emergency_contact_region_id || "")
      );
    }
  }, [regionsQuery.data, isEditMode, fullMemberData]);

  useEffect(() => {
    if (zonesQuery.data && isEditMode && fullMemberData) {
      setValue(
        "emergency_contact_zone_id",
        String(fullMemberData?.emergency_contact_zone_id || "")
      );
    }
  }, [zonesQuery.data, isEditMode, fullMemberData]);

  useEffect(() => {
    if (citiesQuery.data && isEditMode && fullMemberData) {
      setValue(
        "emergency_contact_city_id",
        String(fullMemberData?.emergency_contact_city_id || "")
      );
    }
  }, [citiesQuery.data, isEditMode, fullMemberData]);

  const isOutsideEthiopia =
    String(watch("emergency_contact_country_id")) !== "66";

  return (
    <Grid container columnSpacing={{ xs: 2, md: 12 }}>
      <Grid item xs={12} md={3} alignSelf="center">
        <Typography variant="body1">
          {t("addMembers.step1.EmergencyContact")}
        </Typography>
      </Grid>
      <Grid container item xs={12} md={9} spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("form.common.firstName")}
            name="emergency_contact_first_name"
            error={!!errors.emergency_contact_first_name?.message}
            helperText={errors.emergency_contact_first_name?.message}
            register={register}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("form.common.fatherName")}
            name="emergency_contact_father_name"
            error={!!errors.emergency_contact_father_name?.message}
            helperText={errors.emergency_contact_father_name?.message}
            register={register}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label={t("form.common.grandFatherName")}
            name="emergency_contact_last_name"
            error={!!errors.emergency_contact_last_name?.message}
            helperText={errors.emergency_contact_last_name?.message}
            register={register}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name="emergency_contact_phone"
            control={control}
            render={({ field }) => (
              <PhoneInputField
                label={t("form.common.phone")}
                variant="filled"
                error={!!errors.emergency_contact_phone?.message}
                helperText={errors.emergency_contact_phone?.message}
                {...field}
                required
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label={t("addMembers.step1.Relationship")}
            name="emergency_contact_relationship"
            error={!!errors.emergency_contact_relationship?.message}
            helperText={errors.emergency_contact_relationship?.message}
            register={register}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Select
            isLabelBold={0}
            label={t("form.common.country")}
            name="emergency_contact_country_id"
            options={
              countriesQuery.data?.result?.map(country => ({
                optionLabel: country?.name,
                value: country?.id
              })) || []
            }
            register={register}
            error={!!errors.emergency_contact_country_id?.message}
            helperText={errors.emergency_contact_country_id?.message}
            required
          />
        </Grid>
        {!isOutsideEthiopia && (
          <Grid item xs={12} sm={6} md={3}>
            <Select
              isLabelBold={0}
              label={t("form.common.region")}
              name={`emergency_contact_region_id`}
              options={
                regionsQuery.data?.result?.map(region => ({
                  optionLabel: region?.name,
                  value: region?.id
                })) || []
              }
              register={register}
              error={!!errors.emergency_contact_region_id?.message}
              helperText={errors.emergency_contact_region_id?.message}
              onChange={e => {
                setValue(
                  "emergency_contact_region_id",
                  String(e.target.value),
                  {
                    shouldValidate: true
                  }
                );

                if (
                  String(e.target.value) ===
                  String(ADDRESSES.ADDIS_ABABA.REGION_ID)
                ) {
                  setValue(
                    "emergency_contact_zone_id",
                    String(ADDRESSES.ADDIS_ABABA.ZONE_ID),
                    {
                      shouldValidate: true
                    }
                  );
                  setValue(
                    "emergency_contact_city_id",
                    String(ADDRESSES.ADDIS_ABABA.CITY_ID),
                    {
                      shouldValidate: true
                    }
                  );
                } else {
                  setValue("emergency_contact_zone_id", "", {
                    shouldValidate: true
                  });
                  setValue("emergency_contact_city_id", "", {
                    shouldValidate: true
                  });
                }
              }}
            />
          </Grid>
        )}
        {!isOutsideEthiopia &&
          String(watch("emergency_contact_region_id")) !==
            String(ADDRESSES.ADDIS_ABABA.REGION_ID) && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Select
                  isLabelBold={0}
                  label={t("form.common.zone")}
                  name={`emergency_contact_zone_id`}
                  options={
                    zonesQuery.data?.result?.map(zone => ({
                      optionLabel: zone?.name,
                      value: zone?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.emergency_contact_zone_id?.message}
                  helperText={errors.emergency_contact_zone_id?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Select
                  isLabelBold={0}
                  label={t("form.common.city")}
                  name={`emergency_contact_city_id`}
                  options={
                    citiesQuery.data?.result?.map(city => ({
                      optionLabel: city?.name,
                      value: city?.id
                    })) || []
                  }
                  register={register}
                  error={!!errors.emergency_contact_city_id?.message}
                  helperText={errors.emergency_contact_city_id?.message}
                />
              </Grid>
            </>
          )}

        {isOutsideEthiopia && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="City"
                name="emergency_city"
                error={!!errors.emergency_city?.message}
                helperText={errors.emergency_city?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="State"
                name="emergency_state"
                error={!!errors.emergency_state?.message}
                helperText={errors.emergency_state?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Zip Code"
                name="emergency_zip_code"
                error={!!errors.emergency_zip_code?.message}
                helperText={errors.emergency_zip_code?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Street Address"
                name="emergency_street_address"
                error={!!errors.emergency_street_address?.message}
                helperText={errors.emergency_street_address?.message}
                register={register}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Building Number"
                name="emergency_building_number"
                error={!!errors.emergency_building_number?.message}
                helperText={errors.emergency_building_number?.message}
                register={register}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};
